import React, { useState, useEffect, useMemo } from 'react'
import { V } from '../theme'
import { C, useSignals, MiniChart, GaugeMeter, SignalRow } from './SPXConfluenceLive'


// ── SIMULATED data hook (Tier 2 educational version) ───────────
function useSPXFeed() {
  const [candles, setCandles] = useState([]);
  const [price, setPrice]     = useState(null);
  const [vwap, setVwap]       = useState(null);

  useEffect(() => {
    const seed = () => {
      const base = 5200 + (Math.random() - 0.5) * 200;
      let p = base, cumVol = 0, cumPV = 0;
      return Array.from({ length: 78 }, (_, i) => {
        const change = (Math.random() - 0.48) * 12;
        const open = p, close = p + change;
        const high = Math.max(open, close) + Math.random() * 4;
        const low  = Math.min(open, close) - Math.random() * 4;
        const vol  = Math.floor(60000 + Math.random() * 120000);
        cumVol += vol;
        const typical = (high + low + close) / 3;
        cumPV += typical * vol;
        p = close;
        return { time: Date.now() - (78 - i) * 300000, open, high, low, close, vol, vwap: cumPV / cumVol };
      });
    };
    const data = seed();
    setCandles(data);
    setPrice(data[data.length - 1]?.close);
    setVwap(data[data.length - 1]?.vwap);

    const t = setInterval(() => {
      setCandles(prev => {
        const last = prev[prev.length - 1];
        if (!last) return prev;
        const d = (Math.random() - 0.48) * 5;
        const nc = last.close + d;
        const updated = { ...last, close: nc, high: Math.max(last.high, nc), low: Math.min(last.low, nc) };
        setPrice(nc);
        return [...prev.slice(0, -1), updated];
      });
    }, 2500);
    return () => clearInterval(t);
  }, []);

  return { price, candles, vwap, loading: candles.length === 0, error: null, lastUpdate: new Date().toLocaleTimeString() };
}

// ── SPX CONFLUENCE SIMULATED (Tier 2 educational version) ───────
function SPXConfluence() {
  const { price, candles, vwap, loading } = useSPXFeed();
  const result = useSignals(candles, price || 0, vwap || 0);

  const fontLink = (
    <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap" rel="stylesheet" />
  );

  if (loading || !result || !price) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono',monospace" }}>
      {fontLink}
      <div style={{ color: C.accent, fontSize: '13px', letterSpacing: '0.1em' }}>LOADING SIMULATED DATA...</div>
    </div>
  );

  const { score, bias, signals } = result;
  const biasColor = bias === 'LONG' ? C.bull : bias === 'SHORT' ? C.bear : C.warn;
  const highConf = Math.abs(score) >= 6;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'JetBrains Mono',monospace", color: C.text, padding: '20px 16px', maxWidth: '780px', margin: '0 auto' }}>
      {fontLink}

      {/* Tier 2 info card */}
      <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ fontSize: '9px', letterSpacing: '0.14em', color: V.gold, fontFamily: "'Jost',sans-serif", textTransform: 'uppercase', marginBottom: '6px' }}>Tier 2 — Advanced Tool</div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', color: V.goldL, marginBottom: '8px' }}>SPX Confluence Indicator</div>
        <div style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7, marginBottom: '10px' }}>
          A confluence signal engine for SPX day trading. Combines VWAP, EMA crossovers (9/20/50), RSI, MACD, and volume to generate a directional bias score from −10 to +10. This version uses simulated data for educational practice.
        </div>
        <div style={{ fontSize: '11px', color: V.rose, fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>
          Simulated data only. Not live market prices. Not financial advice.
        </div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.12em', color: C.accent }}>SPX CONFLUENCE</div>
          <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.1em', marginTop: '2px' }}>SIMULATED · 5-MIN</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '26px', fontWeight: 700, color: C.text, lineHeight: 1 }}>{price.toFixed(2)}</div>
          {vwap > 0 && <div style={{ fontSize: '10px', color: C.muted, marginTop: '2px' }}>VWAP {vwap.toFixed(2)}</div>}
          <div style={{ display: 'inline-block', marginTop: '4px', background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)', borderRadius: '20px', padding: '2px 10px', fontSize: '9px', color: C.warn, letterSpacing: '0.1em' }}>● SIM</div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
        <MiniChart candles={candles} price={price} vwap={vwap} />
      </div>

      {/* Gauge */}
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
        <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.12em', marginBottom: '10px' }}>CONFLUENCE SCORE</div>
        <GaugeMeter score={score} />
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <span style={{ background: `${biasColor}18`, border: `1px solid ${biasColor}50`, borderRadius: '6px', padding: '6px 20px', fontSize: '14px', fontWeight: 700, color: biasColor, letterSpacing: '0.12em' }}>{bias}</span>
        </div>
      </div>

      {highConf && (
        <div style={{ background: `${biasColor}10`, border: `1px solid ${biasColor}40`, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '11px', color: biasColor, letterSpacing: '0.06em' }}>
          ⬤ HIGH CONFLUENCE — {bias === 'LONG' ? 'Strong bullish alignment' : 'Strong bearish alignment'}
        </div>
      )}

      {/* Signals */}
      <div style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden', marginBottom: '14px' }}>
        <div style={{ fontSize: '9px', color: C.muted, letterSpacing: '0.12em', padding: '10px 12px 6px' }}>SIGNAL BREAKDOWN</div>
        {Object.entries(signals).map(([key, sig]) => <SignalRow key={key} signal={sig} />)}
      </div>

      <div style={{ fontSize: '9px', color: C.dim, textAlign: 'center', letterSpacing: '0.08em', paddingTop: '8px' }}>
        SIMULATED DATA ONLY · NOT FINANCIAL ADVICE
      </div>
    </div>
  );
}

export default SPXConfluence
